import { type ReactNode, useMemo } from "react";
import { View } from "react-native";

import type { AppError } from "@/api/errors";

import { ConfigError, readPublicConfig } from "@/config";
import { useRetryRestore, useSession } from "@/session/hooks";

import { ConfigErrorScreen } from "./ConfigErrorScreen";
import { ProductHeader } from "./header/ProductHeader";
import { LoadingScreen } from "./LoadingScreen";
import { useScreenStyles } from "./screenStyles";
import { StateView } from "./StateView";

type Props = {
  children: ReactNode;
  /** Injected by tests; the app uses readPublicConfig. */
  readConfig?: () => unknown;
};

/**
 * Root gate inside the providers (FR-012, FR-037): an invalid public config shows the configuration
 * error; while the session restores only the loading screen renders; then the routes. 002: a restore
 * that failed for a reason worth retrying (offline, timeout, 5xx, 429) shows the offline or error
 * state with Try again, never the entry screen (FR-023; constitution VIII).
 */
export function RootShell({ children, readConfig = readPublicConfig }: Props) {
  const configError = useMemo(() => {
    try {
      readConfig();
      return null;
    } catch (error) {
      return error instanceof ConfigError
        ? error
        : new ConfigError("publicConfig", String(error));
    }
  }, [readConfig]);
  const session = useSession();
  const retryRestore = useRetryRestore();

  if (configError) return <ConfigErrorScreen error={configError} />;
  if (session.status === "restoring") {
    if (!session.error) return <LoadingScreen />;
    return (
      <RestoreFailed
        error={session.error}
        onRetry={() => void retryRestore()}
      />
    );
  }
  return <>{children}</>;
}

function RestoreFailed({
  error,
  onRetry,
}: {
  error: AppError;
  onRetry: () => void;
}) {
  const styles = useScreenStyles();
  const offline = error.kind === "offline" || error.kind === "timeout";
  return (
    <View style={styles.screen} testID="restore-failed">
      <ProductHeader />
      {offline ? (
        <StateView status="offline" onRetry={onRetry} />
      ) : (
        <StateView status="error" error={error} onRetry={onRetry} />
      )}
    </View>
  );
}
